import React, { useState } from "react";
import { SomaSeal } from "../components/SomaMarks";
import { Camera } from "lucide-react";

/* /style-lab — visual font + UI chrome chooser so we can decide together */

const FONT_OPTIONS = [
  {
    id: "current",
    label: "A — Current (Italiana + Fraunces)",
    soma: { fontFamily: "'Italiana', serif", letterSpacing: "0.06em", fontWeight: 400 },
    lixir: { fontFamily: "'Fraunces', serif", fontStyle: "italic", fontVariationSettings: "'SOFT' 100, 'WONK' 1" },
    body: "'Outfit', sans-serif",
    eyebrow: "'Outfit', sans-serif",
  },
  {
    id: "im_fell",
    label: "B — IM Fell (17th-century printed book, RUSTIC + characterful)",
    soma: { fontFamily: "'IM Fell DW Pica', serif", letterSpacing: "0.04em", fontWeight: 400 },
    lixir: { fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic" },
    body: "'IM Fell English', serif",
    eyebrow: "'IM Fell English', serif",
  },
  {
    id: "cinzel_cormorant",
    label: "C — Cinzel + Cormorant Infant (apothecary / classical + quirky italic)",
    soma: { fontFamily: "'Cinzel', serif", letterSpacing: "0.10em", fontWeight: 500 },
    lixir: { fontFamily: "'Cormorant Infant', serif", fontStyle: "italic", fontWeight: 400 },
    body: "'Cormorant Infant', serif",
    eyebrow: "'Cinzel', serif",
  },
  {
    id: "yeseva",
    label: "D — Yeseva One (curly vintage, all one font)",
    soma: { fontFamily: "'Yeseva One', serif", letterSpacing: "0.02em", fontWeight: 400 },
    lixir: { fontFamily: "'Yeseva One', serif", fontStyle: "italic", fontWeight: 400 },
    body: "'Outfit', sans-serif",
    eyebrow: "'Outfit', sans-serif",
  },
  {
    id: "forum_upright",
    label: "E — Forum + Cormorant Upright (Roman inscription + delicate italic)",
    soma: { fontFamily: "'Forum', serif", letterSpacing: "0.12em", fontWeight: 400 },
    lixir: { fontFamily: "'Cormorant Upright', serif", fontStyle: "italic", fontWeight: 400 },
    body: "'Cormorant Upright', serif",
    eyebrow: "'Forum', serif",
  },
  {
    id: "marcellus",
    label: "F — Marcellus + Cormorant Infant italic (carved stone + book hand)",
    soma: { fontFamily: "'Marcellus', serif", letterSpacing: "0.08em", fontWeight: 400 },
    lixir: { fontFamily: "'Cormorant Infant', serif", fontStyle: "italic", fontWeight: 400 },
    body: "'Cormorant Infant', serif",
    eyebrow: "'Marcellus', serif",
  },
];

function FontPreviewCard({ opt }) {
  return (
    <div style={{
      padding: "32px 24px",
      background: "linear-gradient(180deg, #160C07 0%, #0E0805 100%)",
      borderRadius: 14,
      border: "1px solid rgba(143, 196, 224, 0.22)",
      textAlign: "center",
      position: "relative",
      overflow: "hidden",
    }}>
      <p style={{
        position: "absolute", top: 10, left: 14,
        fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.1em",
        color: "var(--terracotta-soft)", opacity: 0.8, margin: 0,
      }}>{opt.id}</p>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 14, color: "var(--terracotta-soft)" }}>
        <SomaSeal size={56} />
      </div>
      <p style={{
        fontFamily: opt.eyebrow, fontSize: 10, letterSpacing: "0.34em", textTransform: "uppercase",
        color: "var(--terracotta-soft)", margin: "0 0 14px",
      }}>
        Tasseography for the modern cafe
      </p>
      <h1 style={{ margin: 0, color: "var(--cream)", fontSize: 46, lineHeight: 1 }}>
        <span style={opt.soma}>SOMA</span>{" "}
        <span style={{ ...opt.lixir, color: "var(--terracotta-soft)" }}>Lixir</span>
      </h1>
      <p style={{
        fontFamily: opt.body, fontSize: 14, color: "var(--cream-soft)",
        marginTop: 22, lineHeight: 1.7, fontStyle: opt.body.includes("Cormorant") ? "italic" : "normal",
      }}>
        Welcome. Take a breath. You made it here. What you're about to do is older than your great-grandmother's great-grandmother.
      </p>
      <p style={{
        fontFamily: opt.body === "'Outfit', sans-serif" ? "'Fraunces', serif" : opt.body,
        fontStyle: "italic", fontSize: 14, color: "var(--terracotta-soft)",
        margin: "22px 0 0", padding: "14px 18px",
        borderTop: "1px solid rgba(143, 196, 224, 0.22)",
        borderBottom: "1px solid rgba(143, 196, 224, 0.22)",
      }}>
        The tradition never claimed the cup held power.
      </p>
    </div>
  );
}

/* ------ Upload zone — three style options ------ */

function UploadOrganic() {
  return (
    <div style={{ position: "relative", padding: 32, background: "rgba(241, 228, 208, 0.04)", borderRadius: 0 }}>
      {/* Hand-sketched wavy border via SVG */}
      <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }} preserveAspectRatio="none" viewBox="0 0 200 240">
        <path
          d="M4,8 Q12,4 22,6 T46,5 T72,6 T108,5 T142,6 T172,5 T196,7 L196,232 Q190,236 178,234 T146,235 T108,234 T70,235 T34,234 T4,232 Z"
          fill="none" stroke="rgba(143, 196, 224, 0.55)" strokeWidth="1.2" strokeDasharray="0.5 0"
        />
      </svg>
      {/* Photo corner mounts */}
      {[
        { top: 8, left: 8, rot: 0 },
        { top: 8, right: 8, rot: 90 },
        { bottom: 8, left: 8, rot: -90 },
        { bottom: 8, right: 8, rot: 180 },
      ].map((p, i) => (
        <svg key={i} width="20" height="20" viewBox="0 0 20 20" style={{ position: "absolute", ...p, transform: `rotate(${p.rot}deg)` }}>
          <path d="M2,2 L18,2 L18,4 L4,4 L4,18 L2,18 Z" fill="rgba(241, 228, 208, 0.35)" />
        </svg>
      ))}
      <div style={{ textAlign: "center", padding: "24px 12px", position: "relative" }}>
        <Camera size={28} color="var(--terracotta-soft)" style={{ margin: "0 auto 12px" }} />
        <p style={{ fontFamily: "'Fraunces', serif", fontStyle: "italic", fontSize: 18, color: "var(--cream)", margin: 0, fontVariationSettings: "'SOFT' 100, 'WONK' 1" }}>
          place your cup here
        </p>
        <p style={{ fontFamily: "'Fraunces', serif", fontStyle: "italic", fontSize: 13, color: "var(--dim)", margin: "8px 0 0" }}>
          grounds, leaves, dregs — whatever's left
        </p>
      </div>
    </div>
  );
}

function UploadY2K() {
  return (
    <div style={{
      background: "#0A0604",
      border: "1px solid var(--terracotta-soft)",
      borderRadius: 0,
      padding: 0,
      fontFamily: "'Space Mono', monospace",
      position: "relative",
    }}>
      {/* Terminal header bar */}
      <div style={{
        background: "var(--terracotta-soft)",
        color: "#0A0604",
        padding: "5px 12px",
        fontFamily: "'Space Mono', monospace",
        fontSize: 10,
        letterSpacing: "0.16em",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}>
        <span>SOMA://CUP_CAPTURE.EXE</span>
        <span>● ● ●</span>
      </div>
      <div style={{ padding: "26px 22px", textAlign: "center", position: "relative" }}>
        {/* corner brackets */}
        <span style={{ position: "absolute", top: 8, left: 8, color: "var(--terracotta-soft)", fontFamily: "'VT323', monospace", fontSize: 22, lineHeight: 1 }}>┌</span>
        <span style={{ position: "absolute", top: 8, right: 8, color: "var(--terracotta-soft)", fontFamily: "'VT323', monospace", fontSize: 22, lineHeight: 1 }}>┐</span>
        <span style={{ position: "absolute", bottom: 8, left: 8, color: "var(--terracotta-soft)", fontFamily: "'VT323', monospace", fontSize: 22, lineHeight: 1 }}>└</span>
        <span style={{ position: "absolute", bottom: 8, right: 8, color: "var(--terracotta-soft)", fontFamily: "'VT323', monospace", fontSize: 22, lineHeight: 1 }}>┘</span>

        <p style={{ fontFamily: "'VT323', monospace", fontSize: 28, color: "var(--terracotta-soft)", margin: 0, letterSpacing: "0.02em" }}>
          &gt; INSERT IMAGE_
        </p>
        <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "var(--muted)", margin: "12px 0 0", letterSpacing: "0.1em" }}>
          [ .JPG .PNG .WEBP ]
        </p>
        <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "var(--dim)", margin: "6px 0 0", letterSpacing: "0.06em", fontStyle: "italic" }}>
          // grounds, leaves, dregs accepted
        </p>
      </div>
      {/* scanline overlay effect */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: "repeating-linear-gradient(180deg, transparent 0, transparent 2px, rgba(143, 196, 224, 0.03) 2px, rgba(143, 196, 224, 0.03) 3px)",
      }} />
    </div>
  );
}

function UploadStamped() {
  // Third option — a "paper card with wax seal" feel
  return (
    <div style={{
      background: "#EAE2DD",
      color: "#21130C",
      padding: 32,
      position: "relative",
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><filter id='n'><feTurbulence baseFrequency='0.85' numOctaves='2'/><feColorMatrix values='0 0 0 0 0.4 0 0 0 0 0.3 0 0 0 0 0.2 0 0 0 0.2 0'/></filter><rect width='100' height='100' filter='url(%23n)'/></svg>\")",
      backgroundBlendMode: "multiply",
    }}>
      {/* stamped seal in corner */}
      <div style={{
        position: "absolute", top: -14, right: -14, width: 60, height: 60,
        background: "var(--rust)",
        borderRadius: "50%",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "var(--cream)",
        fontFamily: "'IM Fell English', serif",
        fontStyle: "italic", fontSize: 11,
        transform: "rotate(8deg)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
      }}>vol. I</div>

      <div style={{ textAlign: "center" }}>
        <p style={{ fontFamily: "'IM Fell English', serif", fontSize: 11, letterSpacing: "0.3em", textTransform: "uppercase", margin: 0, color: "var(--rust)" }}>
          From the inside of your cup
        </p>
        <p style={{ fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic", fontSize: 26, color: "#21130C", margin: "18px 0 8px", lineHeight: 1.2 }}>
          Affix your photograph here
        </p>
        <div style={{ display: "flex", justifyContent: "center", margin: "14px 0" }}>
          <div style={{
            width: 180, height: 140,
            border: "1px solid rgba(33, 19, 12, 0.3)",
            background: "rgba(33, 19, 12, 0.04)",
            display: "flex", alignItems: "center", justifyContent: "center",
            position: "relative",
          }}>
            {/* photo corner marks */}
            {[
              { top: 4, left: 4 }, { top: 4, right: 4 }, { bottom: 4, left: 4 }, { bottom: 4, right: 4 },
            ].map((p, i) => (
              <span key={i} style={{ position: "absolute", ...p, width: 14, height: 14, borderTop: i < 2 ? "1.5px solid #5A2E22" : "none", borderBottom: i >= 2 ? "1.5px solid #5A2E22" : "none", borderLeft: (i === 0 || i === 2) ? "1.5px solid #5A2E22" : "none", borderRight: (i === 1 || i === 3) ? "1.5px solid #5A2E22" : "none" }} />
            ))}
            <Camera size={20} color="#5A2E22" style={{ opacity: 0.7 }} />
          </div>
        </div>
        <p style={{ fontFamily: "'IM Fell English', serif", fontStyle: "italic", fontSize: 12, color: "#5A2E22", margin: 0 }}>
          tap, snap, or upload — whatever's left of it
        </p>
      </div>
    </div>
  );
}

export default function StyleLabPage() {
  return (
    <div style={{ background: "#0E0805", minHeight: "100vh", color: "var(--cream)", padding: "40px 20px 80px" }}>
      <div className="grain" />
      <div style={{ maxWidth: 560, margin: "0 auto" }}>
        <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.24em", color: "var(--terracotta-soft)", textTransform: "uppercase", marginBottom: 6 }}>
          // style lab
        </p>
        <h1 style={{ fontFamily: "'Italiana', serif", fontSize: 32, margin: 0, color: "var(--cream)" }}>
          Pick the SOMA <span style={{ fontStyle: "italic", color: "var(--terracotta-soft)" }}>look</span>
        </h1>
        <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, color: "var(--cream-soft)", margin: "10px 0 36px", lineHeight: 1.6 }}>
          Six font directions below, then three options for the "drop your cup photo" moment. Tell me which letter + which number you want and I'll lock the whole app to it.
        </p>

        <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.24em", color: "var(--terracotta-soft)", textTransform: "uppercase", marginBottom: 14 }}>
          // 1 / typography
        </p>
        <div style={{ display: "grid", gap: 18, marginBottom: 56 }}>
          {FONT_OPTIONS.map((opt) => (
            <div key={opt.id}>
              <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "var(--muted)", margin: "0 0 8px", letterSpacing: "0.06em" }}>
                {opt.label}
              </p>
              <FontPreviewCard opt={opt} />
            </div>
          ))}
        </div>

        <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.24em", color: "var(--terracotta-soft)", textTransform: "uppercase", marginBottom: 14 }}>
          // 2 / "place your cup" upload styles
        </p>
        <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: "var(--cream-soft)", margin: "0 0 22px", lineHeight: 1.6 }}>
          Same data-testid="upload-zone" element, three completely different feels. Pick whichever matches the brand best — I can also do a mix (e.g. organic frame but with retrotech labels).
        </p>

        <div style={{ display: "grid", gap: 22 }}>
          <div>
            <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "var(--muted)", margin: "0 0 8px", letterSpacing: "0.06em" }}>
              1 — ORGANIC (hand-sketched border + scrapbook photo corners)
            </p>
            <UploadOrganic />
          </div>
          <div>
            <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "var(--muted)", margin: "0 0 8px", letterSpacing: "0.06em" }}>
              2 — Y2K RETROTECH (terminal frame + monospace + scanlines)
            </p>
            <UploadY2K />
          </div>
          <div>
            <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "var(--muted)", margin: "0 0 8px", letterSpacing: "0.06em" }}>
              3 — STAMPED CARD (cream paper, photo corner marks, wax seal — most "old apothecary" of the three)
            </p>
            <UploadStamped />
          </div>
        </div>

        <div style={{
          marginTop: 56, padding: 22,
          border: "1px solid rgba(143, 196, 224, 0.32)",
          borderRadius: 8,
          background: "rgba(143, 196, 224, 0.05)",
        }}>
          <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.24em", color: "var(--terracotta-soft)", textTransform: "uppercase", margin: "0 0 12px" }}>
            // tell me which
          </p>
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, color: "var(--cream-soft)", lineHeight: 1.7, margin: 0 }}>
            Pick one font letter (A–F) and one upload number (1–3). E.g. "B + 3" = IM Fell typography with the stamped apothecary card. Once you pick, I'll apply it to every screen + carry the same treatment to the other form elements (textareas, email inputs, buttons) so the whole thing feels cohesive.
          </p>
        </div>
      </div>
    </div>
  );
}
